import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Share, Copy, ExternalLink, History, Sun, Moon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { StorageManager } from '@/utils/storageManager';
import { useSupabase } from '@/hooks/useSupabase';
import { useChatStorage } from '@/hooks/useChatStorage';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/integrations/supabase/client';

interface ChatHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectChat: (chatId: string) => void;
  isDarkMode: boolean;
}

const storageManager = StorageManager.getInstance();

export function ChatHistoryModal({ isOpen, onClose, onSelectChat, isDarkMode }: ChatHistoryModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sessions, setSessions] = useState<any[]>([]);
  const { toast } = useToast();
  const { setTheme } = useTheme();
  
  // Use Supabase for authenticated users
  const { user } = useSupabase();
  const supabaseChatStorage = useChatStorage(user);

  // Load sessions when modal opens - use Supabase for authenticated users
  React.useEffect(() => {
    if (isOpen) {
      const loadSessions = async () => {
        if (user && supabaseChatStorage.sessions) {
          // Use Supabase sessions for authenticated users
          const sortedSessions = [...supabaseChatStorage.sessions].sort((a, b) => {
            const dateA = new Date(a.updated_at);
            const dateB = new Date(b.updated_at);
            return dateB.getTime() - dateA.getTime();
          });
          setSessions(sortedSessions);
        } else {
          // Fallback to localStorage for guests
          const allSessions = storageManager.getAllSessions();
          const sortedSessions = allSessions.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.timestamp);
            const dateB = new Date(b.updatedAt || b.timestamp);
            return dateB.getTime() - dateA.getTime();
          });
          setSessions(sortedSessions);
        }
      };
      
      loadSessions();
    }
  }, [isOpen, user, supabaseChatStorage.sessions]);

  const filteredSessions = sessions.filter(session =>
    (session.title || 'Untitled Chat').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleShareChat = async (session: any) => {
    try {
      if (!user) {
        toast({
          title: 'Sign in required',
          description: 'Please sign in to generate a shareable link.',
          variant: 'destructive',
        });
        return;
      }

      // Check for existing shared_link
      const { data: existing, error: fetchError } = await supabase
        .from('chat_sessions')
        .select('shared_link, is_shared')
        .eq('id', session.id)
        .single();

      if (fetchError) throw fetchError;

      let shareId = existing?.shared_link as string | null;

      if (!existing?.is_shared || !shareId) {
        // Generate a new share link via RPC and update the session
        const { data: linkData, error: linkError } = await supabase.rpc('generate_share_link');
        if (linkError) throw linkError;

        shareId = linkData as string;
        const now = new Date().toISOString();
        const { error: updateError } = await supabase
          .from('chat_sessions')
          .update({ shared_link: shareId, is_shared: true, share_date: now })
          .eq('id', session.id);
        if (updateError) throw updateError;
      }

      const shareUrl = `https://oreog.netlify.app/s/${shareId}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: `Chat: ${session.title || 'Untitled Chat'}`,
            text: `Check out this AI chat conversation`,
            url: shareUrl,
          });
        } catch (err) {
          await navigator.clipboard.writeText(shareUrl);
          toast({ title: 'Link copied!', description: 'Chat link copied to clipboard' });
        }
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast({ title: 'Link copied!', description: 'Chat link copied to clipboard' });
      }
    } catch (e) {
      console.error('Failed to share chat:', e);
      toast({ title: 'Error', description: 'Failed to generate share link', variant: 'destructive' });
    }
  };

  const handleCopyChat = async (session: any) => {
    const chatContent = session.messages?.map((msg: any) => 
      `${msg.type === 'user' ? 'You' : 'AI'}: ${msg.content}`
    ).join('\n\n') || '';
    
    await navigator.clipboard.writeText(chatContent);
    toast({
      title: "Chat copied!",
      description: "Chat content copied to clipboard",
    });
  };

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / 36e5;
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const titleTextClass = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextClass = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const dialogBgClass = isDarkMode
    ? 'bg-[#101114] border border-[#1f1f23] text-gray-100'
    : 'bg-white border border-gray-200 text-gray-900';
  const inputClass = isDarkMode
    ? 'bg-[#15161a] border border-[#24262b] text-gray-100 placeholder:text-gray-500 focus-visible:ring-gray-600'
    : 'bg-white border border-gray-300 text-gray-900 placeholder:text-gray-500 focus-visible:ring-gray-300';
  const cardClass = isDarkMode
    ? 'bg-[#17181c] border border-[#23252b] hover:bg-[#1e2026]'
    : 'bg-white border border-gray-200 hover:bg-gray-100';
  const iconMutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const themeToggleLabel = isDarkMode ? 'Switch to light mode' : 'Switch to dark mode';

  const handleToggleTheme = () => {
    setTheme(isDarkMode ? 'light' : 'dark');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-[98vw] sm:max-w-2xl max-h-[80vh] overflow-y-hidden rounded-lg px-3 sm:px-4 pt-4 ${dialogBgClass}`}
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle
              className={`flex items-center gap-2 text-base sm:text-lg font-semibold truncate max-w-[78vw] sm:max-w-[480px] ${titleTextClass}`}
            >
              <History className={`w-5 h-5 ${iconMutedClass}`} />
              <span className="truncate">Chat History</span>
            </DialogTitle>
            <DialogDescription className="sr-only">View and search your previous chats.</DialogDescription>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className={`absolute left-3 top-3 h-4 w-4 ${mutedTextClass}`} />
            <Input
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-9 pr-3 ${inputClass}`}
            />
          </div>

          {/* Chat list */}
          <ScrollArea className="h-[60vh] sm:h-96 pr-1 sm:pr-2 scrollbar-none">
            <div className="space-y-2 pt-2 pb-6">
              {filteredSessions.length === 0 ? (
                <div className={`text-center py-8 ${mutedTextClass}`}>
                  {searchTerm ? 'No chats found matching your search' : 'No chat history found'}
                </div>
              ) : (
                filteredSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`flex items-start justify-between gap-3 p-3 rounded-lg transition-colors group ${cardClass}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3
                          className={`font-medium text-sm truncate max-w-[60vw] sm:max-w-[420px] ${titleTextClass}`}
                        >
                          {session.title || 'Untitled Chat'}
                        </h3>
                      </div>
                      <div className={`flex items-center gap-4 text-xs ${mutedTextClass}`}>
                        
                        <span className="flex items-center gap-1">
                          {user ? session.message_count : (session.messageCount || 0)} messages
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShareChat(session)}
                        className={`h-8 w-8 p-0 ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-[#262830]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}
                      >
                        <Share className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onSelectChat(session.id);
                          onClose();
                        }}
                        className={`h-8 w-8 p-0 ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-[#262830]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}